import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { CompletionType, DestroyEvidenceRequest } from '../../../types/custody-actions'
import { usePerformCustodyAction } from '../../../hooks/use-custody-actions'
import { EvidenceItemSelector } from './EvidenceItemSelector'

interface DestroyEvidenceFormProps {
  caseId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

interface DestroyFormState {
  selectedIds: string[]
  completionType: CompletionType
  instructionDate: string
  complianceDate: string
  destructionMethod: string
  authorisingParty: string
  executingParty: string
  notes: string
}

const DATE_SLICE_END = 10

function buildInitialState(): DestroyFormState {
  const today = new Date().toISOString().slice(0, DATE_SLICE_END)
  return {
    selectedIds: [],
    completionType: 'complete',
    instructionDate: today,
    complianceDate: today,
    destructionMethod: '',
    authorisingParty: '',
    executingParty: '',
    notes: '',
  }
}

function buildRequest(state: DestroyFormState): DestroyEvidenceRequest {
  const req: DestroyEvidenceRequest = {
    evidenceNumberIds: state.selectedIds,
    completionType: state.completionType,
    instructionDate: state.instructionDate,
    complianceDate: state.complianceDate,
    destructionMethod: state.destructionMethod,
    authorisingParty: state.authorisingParty,
    executingParty: state.executingParty,
  }
  if (state.notes.length > 0) req.notes = state.notes
  return req
}

type Updater = <K extends keyof DestroyFormState>(key: K, value: DestroyFormState[K]) => void

interface DestroySelectionSectionProps {
  caseId: string
  state: DestroyFormState
  update: Updater
}

function DestroySelectionSection({
  caseId,
  state,
  update,
}: DestroySelectionSectionProps): React.ReactElement {
  return (
    <EvidenceItemSelector
      caseId={caseId}
      selectedIds={state.selectedIds}
      onChange={(ids) => {
        update('selectedIds', ids)
      }}
      filterDestroyed={true}
    />
  )
}

interface DestroyCompletionSectionProps {
  state: DestroyFormState
  update: Updater
}

function DestroyCompletionSection({
  state,
  update,
}: DestroyCompletionSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div>
        <p className="mb-1 text-xs text-[var(--text-muted)]">
          {t('custodyActions.completionType')}
        </p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="dest-completion"
              value="complete"
              checked={state.completionType === 'complete'}
              onChange={() => {
                update('completionType', 'complete')
              }}
            />
            {t('custodyActions.complete')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="dest-completion"
              value="partial"
              checked={state.completionType === 'partial'}
              onChange={() => {
                update('completionType', 'partial')
              }}
            />
            {t('custodyActions.partial')}
          </label>
        </div>
      </div>
      <div className="flex gap-4">
        <div className="flex-1">
          <label htmlFor="dest-idate" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.instructionDate')}
          </label>
          <input
            id="dest-idate"
            type="date"
            value={state.instructionDate}
            onChange={(ev) => {
              update('instructionDate', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
        <div className="flex-1">
          <label htmlFor="dest-cdate" className="mb-1 block text-xs text-[var(--text-muted)]">
            {t('custodyActions.complianceDate')}
          </label>
          <input
            id="dest-cdate"
            type="date"
            value={state.complianceDate}
            onChange={(ev) => {
              update('complianceDate', ev.target.value)
            }}
            className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
          />
        </div>
      </div>
    </>
  )
}

interface DestroyPartiesSectionProps {
  state: DestroyFormState
  update: Updater
  error: string | null
}

function DestroyPartiesSection({
  state,
  update,
  error,
}: DestroyPartiesSectionProps): React.ReactElement {
  const { t } = useTranslation('evidence')

  return (
    <>
      <div>
        <label htmlFor="dest-method" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.destructionMethod')}
        </label>
        <input
          id="dest-method"
          type="text"
          value={state.destructionMethod}
          onChange={(ev) => {
            update('destructionMethod', ev.target.value)
          }}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label htmlFor="dest-auth" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.authorisingParty')}
        </label>
        <input
          id="dest-auth"
          type="text"
          value={state.authorisingParty}
          onChange={(ev) => {
            update('authorisingParty', ev.target.value)
          }}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label htmlFor="dest-exec" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.executingParty')}
        </label>
        <input
          id="dest-exec"
          type="text"
          value={state.executingParty}
          onChange={(ev) => {
            update('executingParty', ev.target.value)
          }}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      <div>
        <label htmlFor="dest-notes" className="mb-1 block text-xs text-[var(--text-muted)]">
          {t('custodyActions.notes')}
        </label>
        <textarea
          id="dest-notes"
          value={state.notes}
          onChange={(ev) => {
            update('notes', ev.target.value)
          }}
          rows={2}
          className="w-full rounded border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm"
        />
      </div>
      {error && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
    </>
  )
}

interface DestroyFormFieldsProps {
  caseId: string
  state: DestroyFormState
  update: Updater
  error: string | null
}

function DestroyFormFields({
  caseId,
  state,
  update,
  error,
}: DestroyFormFieldsProps): React.ReactElement {
  return (
    <div className="space-y-4 overflow-y-auto p-6">
      <DestroySelectionSection caseId={caseId} state={state} update={update} />
      <DestroyCompletionSection state={state} update={update} />
      <DestroyPartiesSection state={state} update={update} error={error} />
    </div>
  )
}

export function DestroyEvidenceForm({
  caseId,
  open,
  onClose,
  onSuccess,
}: DestroyEvidenceFormProps): React.ReactElement | null {
  const { t } = useTranslation('evidence')
  const [state, setState] = useState<DestroyFormState>(buildInitialState)
  const { performing, error, performDestroy, clearError } = usePerformCustodyAction()

  const update = useCallback(
    <K extends keyof DestroyFormState>(key: K, value: DestroyFormState[K]) => {
      setState((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  const handleSubmit = useCallback(async () => {
    const result = await performDestroy(caseId, buildRequest(state))
    if (result !== null) onSuccess()
  }, [state, caseId, performDestroy, onSuccess])

  const handleClose = useCallback(() => {
    clearError()
    onClose()
  }, [clearError, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-lg border border-[var(--border)] bg-[var(--bg-card)] shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--border)] px-6 py-4">
          <h2 className="text-lg font-[var(--font-heading)] font-semibold text-[var(--text-primary)]">
            {t('custodyActions.destroy')}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="rounded p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <DestroyFormFields caseId={caseId} state={state} update={update} error={error} />
        <div className="flex justify-end gap-2 border-t border-[var(--border)] px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            className="rounded border border-[var(--border)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-[var(--text-primary)] uppercase hover:opacity-80"
          >
            {t('custodyActions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              void handleSubmit()
            }}
            disabled={performing || state.selectedIds.length === 0}
            className="rounded bg-[var(--accent)] px-4 py-2 text-xs font-[var(--font-mono)] tracking-wider text-white uppercase hover:opacity-90 disabled:opacity-50"
          >
            {t('custodyActions.submit')}
          </button>
        </div>
      </div>
    </div>
  )
}
